import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class CookieService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly sharedDomain = '.unimedinconfidentes.coop.br';

  // Fallback caso o valor não seja um JWT válido com campo exp (7 dias)
  private readonly fallbackMaxAge = 60 * 60 * 24 * 7;

  /**
   * Extrai o Max-Age diretamente do exp do JWT.
   * Usado apenas no refreshAccessToken, quando a filha recebe um novo accessToken.
   */
  private getMaxAgeFromToken(token: string): number {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return this.fallbackMaxAge;

      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      const payload = JSON.parse(jsonPayload);
      if (payload?.exp) {
        const secondsRemaining = payload.exp - Math.floor(Date.now() / 1000);
        return secondsRemaining > 0 ? secondsRemaining : this.fallbackMaxAge;
      }
    } catch {
      // Token não é JWT ou está malformado
    }
    return this.fallbackMaxAge;
  }

  /**
   * As filhas normalmente SÓ LEEM cookies do SGI.
   * O set() existe apenas para o caso do refreshAccessToken atualizar o accessToken.
   */
  set(key: string, value: string): void {
    if (!this.isBrowser) return;

    const maxAgeSeconds = this.getMaxAgeFromToken(value);
    const hostname = window.location.hostname;
    const isProductionDomain =
      hostname.endsWith(this.sharedDomain) ||
      hostname === this.sharedDomain.substring(1);

    let cookieString = `${key}=${encodeURIComponent(value)}; path=/; SameSite=Lax; Max-Age=${maxAgeSeconds}`;

    if (isProductionDomain) {
      cookieString += `; domain=${this.sharedDomain}; Secure`;
    } else if (window.location.protocol === 'https:') {
      cookieString += '; Secure';
    }

    document.cookie = cookieString;
  }

  get(key: string): string | null {
    if (!this.isBrowser) return null;

    const nameEQ = `${key}=`;
    for (const raw of document.cookie.split(';')) {
      const c = raw.trim();
      if (c.startsWith(nameEQ)) {
        return decodeURIComponent(c.substring(nameEQ.length));
      }
    }
    return null;
  }

  /**
   * As filhas NÃO removem o cookie compartilhado — isso é responsabilidade do SGI.
   * Remove apenas do escopo local para limpar o cache desta app.
   */
  remove(key: string): void {
    if (!this.isBrowser) return;

    // Remove apenas sem domínio (escopo local desta app)
    document.cookie = `${key}=; path=/; Max-Age=0; SameSite=Lax; Secure`;
  }

  clear(prefix: string): void {
    if (!this.isBrowser) return;

    for (const cookie of document.cookie.split(';')) {
      const eqPos = cookie.indexOf('=');
      const name = (eqPos > -1 ? cookie.substring(0, eqPos) : cookie).trim();
      if (name.startsWith(prefix)) {
        this.remove(name);
      }
    }
  }

  // Utilitário de debug — use no console do browser para inspecionar cookies visíveis
  debug(): Record<string, string> {
    if (!this.isBrowser) return {};

    return Object.fromEntries(
      document.cookie.split(';')
        .map(c => c.trim().split('='))
        .filter(([key]) => !!key)
        .map(([key, ...rest]) => [key, decodeURIComponent(rest.join('='))])
    );
  }
}